import React, {useContext} from 'react';
import {Layer} from './layer';
import {LayerPortal} from './layer-portal';
import {useFaqListContext} from './faq-list-context';
import styled, {ThemeContext} from 'styled-components';
import {useLayer} from '../utils/use-layer';
import {useSafeLayerContext} from '../contexts/layer-context';
import {useFeatureAppId} from '../utils/use-feature-app-id';
import {useComponent} from '../component-library';
import {useTrackingFunctions} from '../contexts/tracking-context';
import {TrackingEvent} from '../types';
import {
  EducationalInfoData,
  FaqOrLink,
  FAQSection,
  Global,
} from '@volkswagen-onehub/whitelabel-e-mobility-data-types';
import {useQuestionsData} from './questions-section';

export interface EducationalInfoProps {
  testId?: string;
  closeButtonTestId?: string;
  translations: EducationalInfoData | undefined;
  globalTranslations: Global;
  faqOrLink?: FaqOrLink;
  faqSectionTranslations: FAQSection;
  inverted?: boolean;
  children?: JSX.Element;
}

interface EducationalInfoLayerProps extends EducationalInfoProps {
  disableReturnFocus: () => void;
}

function useFaqId(
  faqSectionTranslations: FAQSection,
  faqNumber: string | undefined
): string | undefined {
  const questionsData = useQuestionsData(faqSectionTranslations);

  if (!faqNumber) {
    return undefined;
  }

  if (questionsData[faqNumber] === undefined) {
    return undefined;
  }

  return questionsData[faqNumber].id;
}

function EducationalInfoLayer(props: EducationalInfoLayerProps): JSX.Element {
  const {refHandle} = useFaqListContext();
  const {
    disableReturnFocus,
    closeButtonTestId,
    translations,
    globalTranslations,
    faqSectionTranslations,
    faqOrLink,
    children,
  } = props;

  const {close} = useSafeLayerContext();

  const handleLinkClick = (targetScrollToAccordionId: string) => {
    disableReturnFocus();
    close();
    refHandle.current?.openAndFocus(targetScrollToAccordionId);
  };

  const Text = useComponent('Text');
  const Button = useComponent('Button');
  const themeContext = useContext(ThemeContext);
  const Link = useComponent('Link');

  const externalLink = faqOrLink && faqOrLink.externalLink;
  const scrollToAccordionId = useFaqId(
    faqSectionTranslations,
    faqOrLink && faqOrLink.linkedFAQSectionNumber
  );

  return (
    <Layer theme={themeContext} closeButtonTestId={closeButtonTestId}>
      <Text variant="copy" whiteSpace="pre-wrap" tag="p">
        {(translations && translations.educationalInfoContent) || ''}
      </Text>
      {children}
      {scrollToAccordionId ? (
        <LinkWrapper>
          <Text variant="copy-medium" bold tag="p">
            <Button
              emphasis="text"
              onClick={() => handleLinkClick(scrollToAccordionId)}
            >
              {translations?.educationalInfoLinkText ||
                globalTranslations.educationalInfoLayer.FAQLink}
            </Button>
          </Text>
        </LinkWrapper>
      ) : externalLink ? (
        <LinkWrapper>
          <Text variant="copy-medium" bold tag="p">
            <Link
              href={externalLink}
              target="_self"
              external
              ariaLabel={globalTranslations.educationalInfoLayer.linkAriaLabel}
            >
              {translations?.educationalInfoLinkText ||
                globalTranslations.educationalInfoLayer.FAQLink}
            </Link>
          </Text>
        </LinkWrapper>
      ) : null}
    </Layer>
  );
}

export const EducationalInfo = React.memo(
  (props: EducationalInfoProps): JSX.Element => {
    const {
      testId,
      closeButtonTestId,
      translations,
      globalTranslations,
      faqOrLink,
      faqSectionTranslations,
      inverted,
      children,
    } = props;
    const closeButtonRef = React.useRef<HTMLButtonElement>(null);
    const disableReturnFocusRef = React.useRef<boolean>(false);
    const invertedTheme = inverted ? inverted : false;
    const onClosed = () => {
      if (!disableReturnFocusRef.current) {
        /**
         * This is a hack necessary for VW because we are only able to set focus
         * on elements after the layer animation is over / after the FocusLock
         * got removed. TODO: upstream fix for VW
         */
        setTimeout(() => {
          closeButtonRef.current?.focus();
        }, 400);
      }
    };

    const featureAppId = useFeatureAppId();

    const layerResult = useLayer({
      type: 'focus',
      layerId: `${featureAppId}_educational-info`,
      closeButtonLabel:
        globalTranslations.educationalInfoLayer.closeButtonLabel,
      disableReturnFocus: true,
      onClosed,
      userCloseable: true,
      usesFooterDisclaimers: false,
    });

    const tracking = useTrackingFunctions();

    const handleClick = () => {
      if (!layerResult.isOpen) {
        try {
          disableReturnFocusRef.current = false;
          layerResult.open();
          tracking[TrackingEvent.EducationalInfoClick]?.({
            label: translations?.educationalInfoInfoButtonLabel || '',
            link: {
              name: translations?.educationalInfoInfoButtonLabel || '',
            },
          });
        } catch (error) {
          console.error(error);
        }
      }
    };

    const disableReturnFocus = () => {
      disableReturnFocusRef.current = true;
    };

    const InfoButton = useComponent('InfoButton');
    const InfoButtonInverted = useComponent('InfoButtonInverted');

    return (
      <>
        <LayerPortal layerResult={layerResult}>
          <EducationalInfoLayer
            testId={testId}
            closeButtonTestId={closeButtonTestId}
            translations={translations}
            globalTranslations={globalTranslations}
            faqOrLink={faqOrLink}
            faqSectionTranslations={faqSectionTranslations}
            inverted={inverted}
            disableReturnFocus={disableReturnFocus}
          >
            {children}
          </EducationalInfoLayer>
        </LayerPortal>
        {!invertedTheme ? (
          <InfoButton
            onClick={handleClick}
            ariaLabel={translations?.educationalInfoInfoButtonLabel || ''}
            testId={props.testId}
            ref={closeButtonRef}
          />
        ) : (
          <InfoButtonInverted
            onClick={handleClick}
            ariaLabel={translations?.educationalInfoInfoButtonLabel || ''}
            testId={testId}
            ref={closeButtonRef}
          />
        )}
      </>
    );
  }
);

EducationalInfo.displayName = 'EducationalInfo';

const LinkWrapper = styled.div`
  padding-top: 15px;
`;
